import { env } from 'core/constants/env.constants';
import { useCallback, useEffect, useRef, useState } from 'react';
import { INACTIVITY_WARNING_MS, MS_PER_MINUTE, MS_PER_SECOND } from './auth.constants';
import { useAuthContext } from './auth.context';
import { secondsUntil } from './inactivity-timer.business';
import { InactivityTimer, startInactivityTimer } from './inactivity-timer.service';
import { signOutAndClearDevice } from './sign-out.service';

export const useInactivityLogout = () => {
  const { isAuthenticated, auth } = useAuthContext();
  const [warningDeadline, setWarningDeadline] = useState<number | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const timer = useRef<InactivityTimer>();
  const latestAuth = useRef(auth);

  useEffect(() => {
    latestAuth.current = auth;
  }, [auth]);

  const signOut = useCallback(() => {
    setWarningDeadline(null);
    void signOutAndClearDevice(latestAuth.current);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      return undefined;
    }
    const inactivityTimer = startInactivityTimer({
      timeoutMs: env.VITE_INACTIVITY_TIMEOUT * MS_PER_MINUTE,
      warningMs: INACTIVITY_WARNING_MS,
      onWarning: deadline => {
        setSecondsLeft(secondsUntil(deadline, Date.now()));
        setWarningDeadline(deadline);
      },
      onTimeout: signOut,
    });
    timer.current = inactivityTimer;
    return () => {
      inactivityTimer.stop();
      timer.current = undefined;
    };
  }, [isAuthenticated, signOut]);

  useEffect(() => {
    if (warningDeadline === null) {
      return undefined;
    }
    const interval = setInterval(
      () => setSecondsLeft(secondsUntil(warningDeadline, Date.now())),
      MS_PER_SECOND
    );
    return () => clearInterval(interval);
  }, [warningDeadline]);

  const staySignedIn = useCallback(() => {
    setWarningDeadline(null);
    timer.current?.restart();
  }, []);

  const signOutNow = useCallback(() => {
    timer.current?.stop();
    signOut();
  }, [signOut]);

  return { isWarningVisible: warningDeadline !== null, secondsLeft, staySignedIn, signOutNow };
};
